import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChallengeSelectDto,
  ChallengeWithCountsDto,
  ChallengesListDto,
  CreateChallengeDto,
  UpdateChallengeDto,
} from '../types/api.types.ts';

export const useGetChallenges = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<ChallengeSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const data = await apiGet<ChallengesListDto>({
        path: '/v1/intel/challenges',
      });
      return data.challenges;
    },
    queryKey: enabled ? queryKeys.challenges.getChallenges : queryKeys.placeholders.default,
  });
};

export const useGetChallenge = (params: { challengeId: string }) => {
  const { apiGet } = useApi();
  const { challengeId } = params;
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated && challengeId);

  return useQuery<ChallengeWithCountsDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      return await apiGet<ChallengeWithCountsDto>({
        path: `/v1/intel/challenges/${challengeId}`,
      });
    },
    queryKey: enabled ? queryKeys.challenges.getChallenge(challengeId) : queryKeys.placeholders.default,
  });
};

export const useCreateChallenge = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.challenges.getChallenges });
  };

  return useMutation<ChallengeSelectDto, Error, CreateChallengeDto>({
    mutationFn: async (formData) => {
      return await apiPost<ChallengeSelectDto>({
        body: formData,
        path: '/v1/intel/challenges',
      });
    },
    onSuccess,
  });
};

export const useUpdateChallenge = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: ChallengeSelectDto) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.challenges.getChallenges });
    queryClient.invalidateQueries({ queryKey: queryKeys.challenges.getChallenge(data.id) });
  };

  return useMutation<ChallengeSelectDto, Error, { challengeId: string } & UpdateChallengeDto>({
    mutationFn: async ({ challengeId, ...formData }) => {
      return await apiPatch<ChallengeSelectDto>({
        body: formData,
        path: `/v1/intel/challenges/${challengeId}`,
      });
    },
    onSuccess,
  });
};

export const useDeleteChallenge = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.challenges.getChallenges });
  };

  return useMutation<void, Error, string>({
    mutationFn: async (challengeId) => {
      await apiDelete({
        path: `/v1/intel/challenges/${challengeId}`,
      });
    },
    onSuccess,
  });
};
